import { Component } from "react";
import moment from "moment";
import { Box, Grid, GridItem, Text } from "@chakra-ui/react";
import CustomPopover from "../../CustomPopover/CustomPopover";
import { Mixpanel } from "../../Services/MixPanel";
import { Message } from "semantic-ui-react";
import { CommonMethodService } from "../../Services/CommonMethodService";
import Datepicker from "../Datepicker/Datepicker";
import DatePickerTabs from "../Tabs/Tabs";
import { separateAMPM } from "../../CommonService";
import AppointmentComponent from "../Appointment/Appointment";
import {
  AppointmentSlot,
  bookingCalendarProps,
  bookingCalendarState,
} from "../../CommonProps";
import { AdminChecklistItems } from "../../CommonProps";
import { isNumber } from "lodash";

export default class BookingCalendar extends Component<
  bookingCalendarProps,
  bookingCalendarState
> {
  datetimeRef: any;

  constructor(props: bookingCalendarProps) {
    super(props);
    let isBooked =
      this.props.estimateDetails.jobNumber > 0 ||
      !this.props.ibsService.isLinkOpenedFromEmail;
    let selectedDropOffDate = null,
      selectedPickupDate =
        this.props.estimateDetails.jobBookingId &&
        this.props.estimateDetails.expectedPickup
          ? moment(this.props.estimateDetails.expectedPickup)
          : null;
    const customQAInfo = this.props.customQADetails;
    this.state = {
      customQADetails: customQAInfo,
      selectedTabIndex: 0,
      dropoffSelectedDate: selectedDropOffDate,
      pickupSelectedDate: selectedPickupDate,
      showBookingSuccessMessage: false,
      isBookingRequested: isBooked,
      showHireCarDialog: false,
      showUberDialog: false,
      selectedDate: this.props.selectedDate,
      bookingAllowance: this.props.bookingAllowance,
      workingDays: 0,
    };
  }

  handleTabChange = (index: number) => {
    this.setState({ selectedTabIndex: index });
  };

  componentDidUpdate(
    prevProps: bookingCalendarProps,
    prevState: bookingCalendarState
  ) {
    const previousCommentValue =
      prevProps?.commentBox?.props?.children?.props?.value;
    const currentCommentValue =
      this.props?.commentBox?.props?.children?.props?.value;
    if (previousCommentValue === currentCommentValue) {
      if (
        prevProps.estimateDetails.jobBookingId !=
        this.props.estimateDetails.jobBookingId
      ) {
        let isRequested = this.props.estimateDetails.jobBookingId != null;
        this.setState({
          dropoffSelectedDate:
            isRequested && this.props.estimateDetails.expectedArrival
              ? moment(this.props.estimateDetails.expectedArrival)
              : null,
          pickupSelectedDate:
            isRequested && this.props.estimateDetails.expectedPickup
              ? moment(this.props.estimateDetails.expectedPickup)
              : null,
          isBookingRequested: isRequested,
          showHireCarDialog: false,
          showUberDialog: false,
          bookingAllowance: this.props.bookingAllowance,
        });
        this.setState({
          bookingAllowance: this.props.bookingAllowance,
        });
      }

      if (prevProps.customQADetails != this.props.customQADetails) {
        this.setState({
          customQADetails: this.props.customQADetails,
        });
      }
      this.setCalendarDate();
    }
    if (prevProps?.slots !== this.props.slots) {
      this.setState({ selectedTabIndex: 0 });
    }
  }

  ifAvailableAppointments() {
    return (
      this.props.availableAppointments.filter((a: any) => {
        if (isNumber(a.budget)) {
          return a.budget > 0;
        }
        return true;
      }).length > 0
    );
  }

  onDateSelected = (date: any) => {
    const isLoading = this.props.availableAppointments?.some(
      (item: any) => item.showLoader
    );
    if (!isLoading) {
      this.setState({
        selectedDate: date,
        dropoffSelectedDate: date,
      });
      this.props.onDateSelected(date);
    }
  };

  fetchEstimatedPickupDate = () => {
    const {
      estimatedArrivalStartInterval,
      estimatedCompletion,
      estimatedCompletionCollectionInterval,
    } = this.props.bookingAllowance;
    const numWorkingDays =
      estimatedCompletion +
      estimatedArrivalStartInterval +
      estimatedCompletionCollectionInterval;
    if (this.state.selectedDate) {
      let currentDate = moment(this.state.selectedDate);
      let remainingWorkingDays = numWorkingDays;
      let days = 0;
      while (remainingWorkingDays > 0) {
        currentDate = currentDate.add(1, "day");
        if (currentDate.day() >= 1 && currentDate.day() <= 5) {
          remainingWorkingDays--;
        }
        days++;
      }

      return { date: currentDate.format("DD/MM/YYYY"), days };
    } else {
      return { date: "", days: 0 };
    }
  };

  phoneCallEvent() {
    Mixpanel.track("Phone Call", {
      status: "Event trigger from booking calendar",
    });
  }

  renderAppointmentSlots = (slots: AppointmentSlot[] | undefined) => (
    <Grid templateColumns="repeat(4, 1fr)" gap={2} mb={2}>
      <GridItem colSpan={12}>
        <Grid
          templateColumns={[
            "repeat(3, 1fr)",
            "repeat(4, 1fr)",
            "repeat(auto, 1fr)",
          ]}
          gap={2}
        >
          {slots?.map(
            ({ time, occupiedDropOff, isDisabled, isLoading, index }, i) => (
              <AppointmentComponent
                key={i}
                time={time}
                index={index || i}
                occupiedDropOff={occupiedDropOff}
                isLoading={isLoading || false}
                isDisabled={isDisabled || false}
                onSlotPicked={this.props.onSlotPicked}
                isSelected={this.props.isSelected?.time === time}
                showLoader={this.props.showLoader}
              />
            )
          )}
        </Grid>
      </GridItem>
    </Grid>
  );

  render() {
    const params = CommonMethodService.params();
    const pickUpObject = this.fetchEstimatedPickupDate();
    const value = (this.state as any)?.customQADetails?.values.find(
      (val: { value: number | boolean }) => typeof val.value === "number"
    )?.value;
    const showMessage =
      this.state?.customQADetails && typeof value === "number" && value > 0;
    const message = `Parts availability approx: ${value} days. We aim to have parts onsite before your booking.`;
    const { am = [], pm = [] } = separateAMPM(this.props?.slots ?? []);
    const isBookingFlow = this.props.estimateDetails.adminCheckList.some(
      (checklistItem: string) =>
        checklistItem.includes(AdminChecklistItems.BOOKING_FLOW)
    );

    const renderCalendarHeader = () => {
      return (
        <>
          {params["step"] !== "booking-inspection" ? (
            <div className="d-flex justify-content-between">
              <div className="px-2">
                {" "}
                Estimated Pick-Up Time:{" "}
                {this.state.selectedDate
                  ? pickUpObject.date
                  : "No date selected"}
              </div>
              <div className="px-2">
                {this.state.selectedDate && isBookingFlow
                  ? "Days: " + pickUpObject.days
                  : ""}
              </div>
            </div>
          ) : undefined}
        </>
      );
    };

    const btnShouldDisabled =
      this.props?.isButtonDisabledForNonStateChanged || false;
    return (
      <div>
        {showMessage && <Message warning header="" content={message} />}
        {params["step"] !== "booking-inspection" && (
          <Text as={GridItem} colSpan={1} mb={1} className="estimated-pickup">
            <CustomPopover
              mainContent={renderCalendarHeader()}
              bodyContent={
                <>
                  <span style={{ width: "100%" }}>
                    This is an estimate only and repair times may vary. For more
                    information please{" "}
                    <a
                      href="tel:0733543711"
                      onClick={() => this.phoneCallEvent()}
                    >
                      {" call for assistance"}
                    </a>
                  </span>
                </>
              }
            ></CustomPopover>
          </Text>
        )}
        <Grid templateColumns="repeat(1, auto)" gap="2">
          <Box as={GridItem}>
            <Datepicker
              onDateSelected={this.onDateSelected}
              availableAppointments={this.props.availableAppointments}
              selectedDate={this.state.selectedDate}
              dropoffSelectedDate={this.state?.dropoffSelectedDate}
              showLoader={this.props.showLoader}
            />
          </Box>
        </Grid>
        {am.length > 0 || pm.length > 0 ? (
          <DatePickerTabs
            selectedTabIndex={this.state.selectedTabIndex}
            onTabChange={this.handleTabChange}
            items={[
              { name: "Morning", content: this.renderAppointmentSlots(am) },
              {
                name: "Afternoon",
                content: this.renderAppointmentSlots(pm),
              },
            ]}
          />
        ) : null}
        <br></br>
        {this.props.commentBox && <>{this.props.commentBox}</>}
        <Grid className="btn-container">
          <button
            className="booking-next-btn"
            onClick={this.props.handleConfirm}
            disabled={
              this.props.showLoader ||
              !this.props.isSelected ||
              btnShouldDisabled
            }
          >
            {this.props.showLoader ? (
              <>
                <span>Loading</span>
                <i
                  className="fa fa-refresh fa-spin"
                  style={{ marginLeft: "5px" }}
                />
              </>
            ) : (
              <>
                {" "}
                <span>Confirm</span>
              </>
            )}
          </button>
        </Grid>
      </div>
    );
  }

  setCalendarDate() {
    const firstAppointment = moment(
      this.props.availableAppointments[0]?.dueDate
    );

    if (firstAppointment.month() !== moment().month()) {
      const month = moment(firstAppointment).startOf("month").toDate();
      this?.datetimeRef?.setViewDate(month);
    }
  }
}
